import React, { useState, useCallback } from "react";
import axios from "axios";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import API_BASE_URL from "../../api";
import "./Contact.css";
import "./Maintenance.css";

function PrivacyModal({ onClose }) {
    return (
        <div className="maint-modal-overlay" onClick={onClose}>
            <div className="maint-modal" onClick={(e) => e.stopPropagation()}>
                <div className="maint-modal-header">
                    <h2>Privacy Policy</h2>
                    <button className="maint-modal-close" onClick={onClose}>
                        <i className="fas fa-times" />
                    </button>
                </div>
                <div className="maint-modal-body">
                    <p><em>Last updated: April 2026</em></p>

                    <h3>1. Who We Are</h3>
                    <p>This website is operated by <strong>Charlton Farm Services</strong>, trading as <strong>Plough Breakers</strong>.</p>
                    <p>
                        <strong>Data Controller:</strong> Charlton Farm Services<br />
                        <strong>Address:</strong> 2 Little Herdhill Way, Kirriemuir, Angus, DD8 5JZ<br />
                        <strong>Website:</strong> ploughbreakers.co.uk
                    </p>
                    <p>We are committed to protecting your personal data and complying with the UK General Data Protection Regulation (UK GDPR) and the Privacy and Electronic Communications Regulations (PECR).</p>

                    <h3>2. What Data We Collect</h3>
                    <ul>
                        <li><strong>Enquiry forms:</strong> Your name, email address, postcode, phone number (if provided), and the content of your message.</li>
                        <li><strong>Usage data:</strong> Pages visited, time spent on pages, browser type, and device information — collected via Google Analytics.</li>
                        <li><strong>Cookie data:</strong> Information stored in cookies on your device as described in Section 5.</li>
                    </ul>

                    <h3>3. How We Use Your Data</h3>
                    <ul>
                        <li>To respond to your product enquiries and general contact messages.</li>
                        <li>To understand how visitors use our website so we can improve it (Google Analytics).</li>
                        <li>To protect our website from spam and abuse (Google reCAPTCHA).</li>
                    </ul>
                    <p>Our lawful basis for processing enquiry data is <strong>legitimate interests</strong>. Our lawful basis for analytics is <strong>consent</strong>, which you provide via our cookie consent banner.</p>

                    <h3>4. How Long We Keep Your Data</h3>
                    <p>Enquiry data is retained for <strong>12 months</strong> from the date of your enquiry, after which it is deleted. Analytics data is retained in accordance with Google Analytics' default retention settings.</p>

                    <h3>5. Cookies</h3>
                    <ul>
                        <li><strong>Strictly necessary cookies:</strong> Required for the website to function. These cannot be disabled.</li>
                        <li><strong>Analytics cookies:</strong> Set by Google Analytics. Only set with your consent.</li>
                        <li><strong>reCAPTCHA cookies:</strong> Set by Google reCAPTCHA to protect our forms from spam.</li>
                    </ul>

                    <h3>6. Third Parties</h3>
                    <ul>
                        <li><strong>Google Analytics</strong> — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
                        <li><strong>Google reCAPTCHA</strong> — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
                        <li><strong>Cookiebot</strong> — <a href="https://www.cookiebot.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer">Cookiebot's Privacy Policy</a>.</li>
                    </ul>
                    <p>We do not sell or share your personal data with any other third parties.</p>

                    <h3>7. Your Rights</h3>
                    <ul>
                        <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
                        <li><strong>Right to rectification</strong> — ask us to correct inaccurate data.</li>
                        <li><strong>Right to erasure</strong> — ask us to delete your personal data.</li>
                        <li><strong>Right to restrict processing</strong> — ask us to limit how we use your data.</li>
                        <li><strong>Right to object</strong> — object to processing on the basis of legitimate interests.</li>
                        <li><strong>Right to withdraw consent</strong> — withdraw at any time via the cookie banner.</li>
                    </ul>
                    <p>To exercise any of these rights, contact us at the address in Section 1. We will respond within 30 days.</p>
                    <p>You also have the right to lodge a complaint with the <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO)</a>.</p>

                    <h3>8. Changes to This Policy</h3>
                    <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated date.</p>
                </div>
            </div>
        </div>
    );
}

export default function Maintenance() {
    const [name, setName]                   = useState("");
    const [email, setEmail]                 = useState("");
    const [phone, setPhone]                 = useState("");
    const [message, setMessage]             = useState("");
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [loading, setLoading]             = useState(false);
    const [success, setSuccess]             = useState("");
    const [error, setError]                 = useState("");
    const [showPrivacy, setShowPrivacy]     = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!email && !phone) {
            setError("Please provide at least one contact method — email or phone.");
            return;
        }
        if (!privacyAgreed) {
            setError("Please agree to the Privacy Policy before submitting.");
            return;
        }
        if (!executeRecaptcha) {
            setError("reCAPTCHA not ready. Please try again.");
            return;
        }

        try {
            setLoading(true);
            const token = await executeRecaptcha('contact_form');
            await axios.post(`${API_BASE_URL}/api/contact`, {
                name, email, phone, message, recaptchaToken: token,
            });
            setSuccess("Thanks — we've received your enquiry and will be in touch shortly.");
            setName(""); setEmail(""); setPhone(""); setMessage(""); setPrivacyAgreed(false);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to send message. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, name, email, phone, message, privacyAgreed]);

    return (
        <div className="maint-page">
            {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}

            <div className="maint-content">

                {/* LOGO */}
                <div className="maint-logo">
                    <img src="/images/PloughBreakersLogo.svg" alt="Plough Breakers" />
                </div>

                <h1>We're Currently Down for Maintenance</h1>
                <p className="maint-intro">
                    We'll be back up shortly. In the meantime, if you need a Kverneland plough part, please get in touch below and we'll get back to you as soon as possible.
                </p>

                {/* CONTACT LINKS */}
                <div className="maint-contact-links">
                    <a href="tel:07970506155" className="maint-contact-btn maint-btn-call">
                        <i className="fas fa-phone-alt" /> 07970 506155
                    </a>
                    <a href="https://wa.me/447970506155" className="maint-contact-btn maint-btn-whatsapp" target="_blank" rel="noopener noreferrer">
                        <i className="fab fa-whatsapp" /> WhatsApp
                    </a>
                    <a href="mailto:info@ploughbreakers.co.uk" className="maint-contact-btn maint-btn-email">
                        <i className="fas fa-envelope" /> info@ploughbreakers.co.uk
                    </a>
                </div>

                {error   && <p className="contact-error">{error}</p>}
                {success && <p className="contact-success">{success}</p>}

                <h2>Enquire About a Part</h2>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <label>
                        Name *
                        <input value={name} onChange={(e) => setName(e.target.value)} required />
                    </label>

                    <div className="contact-form-row">
                        <label>
                            Email
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" />
                        </label>
                        <label>
                            Phone
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07700 000000" />
                        </label>
                    </div>
                    <p className="contact-form-hint">* At least one contact method required</p>

                    <label>
                        Message *
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Please describe the part you're looking for…"
                            rows={6}
                            required
                        />
                    </label>

                    <label className="contact-privacy-check">
                        <input
                            type="checkbox"
                            checked={privacyAgreed}
                            onChange={(e) => setPrivacyAgreed(e.target.checked)}
                            required
                        />
                        <span>
                            I have read and agree to the{" "}
                            <button type="button" className="maint-privacy-link" onClick={() => setShowPrivacy(true)}>
                                Privacy Policy
                            </button>
                        </span>
                    </label>

                    <button type="submit" disabled={loading}>
                        {loading ? "Sending..." : "Send Enquiry"}
                    </button>

                    <p className="contact-privacy-note">
                        By submitting this form you agree to our{" "}
                        <button type="button" className="maint-privacy-link" onClick={() => setShowPrivacy(true)}>
                            Privacy Policy
                        </button>.
                    </p>
                </form>
            </div>
        </div>
    );
}